/**
 * Card para testar envio de mensagem
 * Permite enviar mensagem de teste para verificar funcionamento da inbox
 * Suporta WhatsApp (telefone) e Instagram (IGSID)
 * 
 * @version 1.1.0
 * @date 2026-01-17
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Send, 
  Loader2,
  CheckCircle2,
  XCircle,
  Phone,
  Instagram,
  AlertCircle,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from '@/components/ui/phone-input';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { TestMessageResult } from '@/hooks/useInboxDetails';

// Logger do módulo
const logger = createLogger('TestMessageCard');

// ============================================================================
// INTERFACES
// ============================================================================

interface TestMessageCardProps {
  onSendTestMessage: (phone: string, content?: string) => Promise<TestMessageResult>;
  hasCredentials: boolean;
  disabled?: boolean;
  providerSlug?: string;  // Para identificar o provider (meta-instagram, z-api, etc)
  inboxId?: string;       // Para buscar contatos recentes do Instagram
}

// Interface para contatos recentes do Instagram
interface RecentContact {
  id: string;
  phone: string; // No caso do Instagram, armazena o IGSID
  name: string | null;
}

// ============================================================================
// COMPONENTE
// ============================================================================

export function TestMessageCard({ 
  onSendTestMessage,
  hasCredentials,
  disabled = false,
  providerSlug,
  inboxId
}: TestMessageCardProps) {
  const { t } = useTranslation('pages');
  
  // Verificar se é Instagram
  const isInstagram = providerSlug === 'meta-instagram';
  
  // Estados locais
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<TestMessageResult | null>(null);
  
  // Estados específicos para Instagram
  const [selectedContact, setSelectedContact] = useState<string>('');
  const [recentContacts, setRecentContacts] = useState<RecentContact[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  // ============================================================================
  // EFEITO - Buscar contatos recentes do Instagram
  // ============================================================================
  
  useEffect(() => {
    if (isInstagram && inboxId) {
      fetchRecentContacts();
    }
  }, [isInstagram, inboxId]);

  /**
   * Busca os contatos recentes que têm conversas ativas na inbox Instagram
   * Esses contatos já iniciaram conversa, então podem receber mensagens
   */
  const fetchRecentContacts = async () => {
    if (!inboxId) return;
    
    setIsLoadingContacts(true);
    try {
      logger.debug('Buscando contatos recentes para inbox Instagram:', inboxId);
      
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          contact:contacts(id, phone, name)
        `)
        .eq('inbox_id', inboxId)
        .not('contact_id', 'is', null)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(10);

      if (error) {
        logger.error('Erro ao buscar contatos recentes:', error.message);
        return;
      }

      // Extrair contatos únicos (pode haver conversas duplicadas)
      const contactsMap = new Map<string, RecentContact>();
      data?.forEach((item: any) => {
        if (item.contact && item.contact.phone) {
          contactsMap.set(item.contact.id, {
            id: item.contact.id,
            phone: item.contact.phone,
            name: item.contact.name
          });
        }
      });

      const contacts = Array.from(contactsMap.values());
      logger.debug(`Encontrados ${contacts.length} contatos recentes`);
      setRecentContacts(contacts);
      
    } catch (err) {
      logger.error('Erro ao buscar contatos:', err);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Handler para enviar mensagem de teste
   * Para Instagram usa IGSID, para WhatsApp usa telefone
   */
  const handleSend = async () => {
    // Determinar destinatário baseado no provider
    const recipient = isInstagram 
      ? (selectedContact || phone.trim())
      : phone.replace(/\D/g, ''); // Remove formatação para WhatsApp
    
    if (!recipient) return;
    
    setIsSending(true);
    setResult(null);
    
    try {
      logger.debug(`Enviando mensagem de teste para: ${recipient} (${isInstagram ? 'Instagram' : 'WhatsApp'})`);
      
      const sendResult = await onSendTestMessage(
        recipient,
        message.trim() || undefined
      );
      setResult(sendResult);
      
      // Limpar campos se sucesso
      if (sendResult.success) {
        setPhone('');
        setMessage('');
        setSelectedContact('');
      }
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Handler para seleção de contato no select
   * Atualiza tanto o selectedContact quanto limpa o input manual
   */
  const handleContactSelect = (value: string) => {
    setSelectedContact(value);
    setPhone(''); // Limpa input manual quando seleciona do dropdown
  };

  // ============================================================================
  // VALIDAÇÕES
  // ============================================================================

  // Verificar se pode enviar - lógica diferente para Instagram vs WhatsApp
  const canSend = isInstagram
    ? (phone.trim().length > 0 || selectedContact.length > 0) && hasCredentials && !disabled
    : phone.trim().length >= 10 && hasCredentials && !disabled;

  // ============================================================================
  // RENDERIZAÇÃO
  // ============================================================================

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Send className="h-4 w-4 text-primary" />
          {t('inboxDetails.testMessage', 'Testar Envio de Mensagem')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Resultado do envio */}
        {result && (
          <Alert variant={result.success ? 'default' : 'destructive'}>
            {result.success ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              {result.success ? (
                <span>
                  {t('inboxDetails.messageSent', 'Mensagem enviada com sucesso!')}
                  {result.message_id && (
                    <span className="block text-xs text-muted-foreground mt-1">
                      ID: {result.message_id.substring(0, 20)}...
                    </span>
                  )}
                </span>
              ) : (
                <span>
                  {t('inboxDetails.messageFailed', 'Falha ao enviar mensagem')}
                  {result.error && (
                    <span className="block text-xs mt-1">
                      {result.error}
                    </span>
                  )}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Campo de destinatário - diferente para Instagram vs WhatsApp */}
        {isInstagram ? (
          <div className="space-y-3">
            <Label className="flex items-center gap-1">
              <Instagram className="h-3 w-3" />
              {t('inboxDetails.instagramRecipient', 'Destinatário Instagram')}
            </Label>
            
            {/* Select de contatos recentes */}
            {recentContacts.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {t('inboxDetails.recentContacts', 'Contatos recentes')}
                </Label>
                <Select 
                  value={selectedContact} 
                  onValueChange={handleContactSelect}
                  disabled={isSending || disabled || !hasCredentials}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('inboxDetails.selectContact', 'Selecione um contato')} />
                  </SelectTrigger>
                  <SelectContent>
                    {recentContacts.map(contact => (
                      <SelectItem key={contact.id} value={contact.phone}>
                        <div className="flex items-center gap-2">
                          <span>{contact.name || 'Sem nome'}</span>
                          <span className="text-xs text-muted-foreground">
                            ({contact.phone.substring(0, 8)}...)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Indicador de carregamento */}
            {isLoadingContacts && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('common.loading', 'Carregando...')}
              </div>
            )}

            {/* Sem contatos recentes */}
            {!isLoadingContacts && recentContacts.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {t('inboxDetails.noRecentContacts', 'Nenhum contato recente encontrado. Digite o IGSID manualmente.')}
              </p>
            )}
            
            {/* Input manual de IGSID */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t('inboxDetails.manualIgsid', 'Ou digite o IGSID manualmente')}
              </Label>
              <Input
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setSelectedContact(''); // Limpa seleção quando digita
                }}
                placeholder="IGSID (ex: 6170625683098876)"
                disabled={isSending || disabled || !hasCredentials}
              />
            </div>
            
            {/* Alerta informativo sobre restrição do Instagram */}
            <Alert variant="default" className="py-2 bg-muted/50">
              <AlertCircle className="h-3 w-3" />
              <AlertDescription className="text-xs">
                {t('inboxDetails.instagramRestriction', 'Instagram só permite enviar mensagens para usuários que já iniciaram uma conversa (janela de 24h).')}
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          /* Campo de telefone para WhatsApp */
          <div className="space-y-2">
            <Label htmlFor="test-phone" className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              {t('inboxDetails.destinationNumber', 'Número de destino')}
            </Label>
            <PhoneInput
              id="test-phone"
              value={phone}
              onChange={setPhone}
              placeholder="+55 62 99999-9999"
              disabled={isSending || disabled || !hasCredentials}
            />
            <p className="text-xs text-muted-foreground">
              {t('inboxDetails.phoneHint', 'Inclua o código do país e DDD')}
            </p>
          </div>
        )}

        {/* Campo de mensagem (opcional) */}
        <div className="space-y-2">
          <Label htmlFor="test-message">
            {t('inboxDetails.testMessageContent', 'Mensagem de teste')} 
            <span className="text-muted-foreground font-normal ml-1">
              ({t('common.optional', 'opcional')})
            </span>
          </Label>
          <Textarea
            id="test-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('inboxDetails.defaultTestMessage', 'Teste de conexão - UaizyChat ✅')}
            disabled={isSending || disabled || !hasCredentials}
            rows={2}
            className="resize-none"
          />
        </div>

        {/* Botão de envio */}
        <Button 
          onClick={handleSend}
          disabled={!canSend || isSending}
          className="w-full"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {isSending 
            ? t('inboxDetails.sending', 'Enviando...') 
            : t('inboxDetails.sendTestMessage', 'Enviar Mensagem de Teste')
          }
        </Button>

        {/* Aviso se não tem credenciais */}
        {!hasCredentials && (
          <p className="text-sm text-muted-foreground text-center">
            {t('inboxDetails.configureCredentialsFirst', 'Configure as credenciais primeiro para enviar mensagens de teste.')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
